'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdTableChart } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    createPriceTable,
    updatePriceTable,
    getPriceTable,
    CreatePriceTableData,
    UpdatePriceTableData,
    PriceTableType,
    PriceTableStatus,
    PRICE_TABLE_TYPE_LABELS,
    PRICE_TABLE_STATUS_LABELS,
} from '@/lib/api/billing/price-table-api'
import { getHealthPlans, HealthPlan } from '@/lib/api/medical/health-plan-api'
import { format } from 'date-fns'

interface FormData {
    name: string
    type: PriceTableType
    status: PriceTableStatus
    healthPlanId: string
    version: string
    validFrom: string
    validUntil: string
    baseMultiplier: string
}

export default function PriceTableFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [healthPlans, setHealthPlans] = useState<HealthPlan[]>([])

    const [formData, setFormData] = useState<FormData>({
        name: '',
        type: 'CBHPM',
        status: 'DRAFT',
        healthPlanId: '',
        version: '',
        validFrom: format(new Date(), 'yyyy-MM-dd'),
        validUntil: '',
        baseMultiplier: '1',
    })

    useEffect(() => {
        if (user?.companyId) {
            loadHealthPlans()
            if (isEditing && id) {
                loadPriceTable()
            }
        }
    }, [id, isEditing, user?.companyId])

    const loadHealthPlans = async () => {
        if (!user?.companyId) return
        try {
            const response = await getHealthPlans(user.organizationId)
            if (response.success) {
                setHealthPlans(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao carregar convênios:', error)
        }
    }

    const loadPriceTable = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getPriceTable(parseInt(id, 10))
            if (response.success && response.result) {
                const table = response.result
                setFormData({
                    name: table.name || '',
                    type: table.type || 'CBHPM',
                    status: table.status || 'DRAFT',
                    healthPlanId: table.healthPlanId?.toString() || '',
                    version: table.version || '',
                    validFrom: table.validFrom
                        ? format(new Date(table.validFrom), 'yyyy-MM-dd')
                        : '',
                    validUntil: table.validUntil
                        ? format(new Date(table.validUntil), 'yyyy-MM-dd')
                        : '',
                    baseMultiplier: table.baseMultiplier?.toString() || '1',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar tabela')
                navigate('/billing/price-tables')
            }
        } catch (error) {
            toast.error('Erro ao carregar tabela')
            navigate('/billing/price-tables')
        } finally {
            setLoadingData(false)
        }
    }

    const updateFormField = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            toast.error('Nome é obrigatório')
            return false
        }
        if (!formData.validFrom) {
            toast.error('Data de início de vigência é obrigatória')
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuario nao autenticado')
            return
        }

        setLoading(true)
        try {
            if (isEditing && id) {
                const updateData: UpdatePriceTableData = {
                    name: formData.name.trim(),
                    type: formData.type,
                    status: formData.status,
                    healthPlanId: formData.healthPlanId
                        ? parseInt(formData.healthPlanId, 10)
                        : undefined,
                    version: formData.version.trim() || undefined,
                    validFrom: formData.validFrom || undefined,
                    validUntil: formData.validUntil || undefined,
                    baseMultiplier: formData.baseMultiplier
                        ? parseFloat(formData.baseMultiplier)
                        : undefined,
                }

                const response = await updatePriceTable(
                    parseInt(id, 10),
                    updateData
                )
                if (response.success) {
                    toast.success('Tabela atualizada com sucesso!')
                    navigate(`/billing/price-tables/${id}`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar tabela')
                }
            } else {
                const createData: CreatePriceTableData = {
                    name: formData.name.trim(),
                    type: formData.type,
                    status: formData.status,
                    healthPlanId: formData.healthPlanId
                        ? parseInt(formData.healthPlanId, 10)
                        : undefined,
                    version: formData.version.trim() || undefined,
                    validFrom: formData.validFrom || undefined,
                    validUntil: formData.validUntil || undefined,
                    baseMultiplier: formData.baseMultiplier
                        ? parseFloat(formData.baseMultiplier)
                        : undefined,
                }

                const response = await createPriceTable(createData)
                if (response.success && response.result) {
                    toast.success('Tabela criada com sucesso!')
                    navigate(`/billing/price-tables/${response.result.id}`)
                } else {
                    toast.error(response.error || 'Erro ao criar tabela')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar tabela')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados da tabela...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/billing/price-tables')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                        <MdTableChart className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing
                                ? 'Editar Tabela de Preços'
                                : 'Nova Tabela de Preços'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize as informações da tabela'
                                : 'Preencha os dados da nova tabela'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações da Tabela
                        </h2>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Nome da tabela de preços"
                                value={formData.name}
                                onChange={e =>
                                    updateFormField('name', e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Tipo
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.type}
                                    onChange={e =>
                                        updateFormField(
                                            'type',
                                            e.target.value as PriceTableType
                                        )
                                    }
                                >
                                    {(
                                        Object.keys(
                                            PRICE_TABLE_TYPE_LABELS
                                        ) as PriceTableType[]
                                    ).map(type => (
                                        <option key={type} value={type}>
                                            {PRICE_TABLE_TYPE_LABELS[type]}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Status
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.status}
                                    onChange={e =>
                                        updateFormField(
                                            'status',
                                            e.target.value as PriceTableStatus
                                        )
                                    }
                                >
                                    {(
                                        Object.keys(
                                            PRICE_TABLE_STATUS_LABELS
                                        ) as PriceTableStatus[]
                                    ).map(status => (
                                        <option key={status} value={status}>
                                            {PRICE_TABLE_STATUS_LABELS[status]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Convênio
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.healthPlanId}
                                    onChange={e =>
                                        updateFormField(
                                            'healthPlanId',
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="">
                                        Particular (sem convênio)
                                    </option>
                                    {healthPlans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Versão
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: 2024.1"
                                    value={formData.version}
                                    onChange={e =>
                                        updateFormField('version', e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Vigência Início{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.validFrom}
                                    onChange={e =>
                                        updateFormField(
                                            'validFrom',
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Vigência Fim
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.validUntil}
                                    onChange={e =>
                                        updateFormField(
                                            'validUntil',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Multiplicador Base
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="input input-bordered w-full"
                                    placeholder="1.00"
                                    value={formData.baseMultiplier}
                                    onChange={e =>
                                        updateFormField(
                                            'baseMultiplier',
                                            e.target.value
                                        )
                                    }
                                />
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Fator multiplicador aplicado aos valores
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/billing/price-tables')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
